"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import { isDockVisible } from "@/lib/home-flags";
import { isDockOnTaskEnabled } from "@/components/composer/dock-on-task-flags";
import { onTaskRoute } from "@/lib/composer/screen-context";
import { useTaskStore } from "@/stores/taskStore";
import { useComputerStore } from "@/stores/computerStore";
import { useFloatingDockOn, useLineOn } from "@/lib/home-flags-hooks";
import { useDockPad } from "./useDockPad";
import { useDockWindowRect } from "./useDockWindowRect";
import { useComposerDraftStore } from "@/stores/composerDraftStore";
import { useComposerFileDrop } from "@/components/message-input/useComposerFileDrop";
import { TrayIsland } from "@/components/composer/TrayIsland";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useFleet } from "@/components/composer/useFleet";
import { BashPill } from "@/components/bash-home/BashPill";
import { primeMic } from "@/lib/voice/mic";
import {
  beginDockDrag,
  onDockSummon,
  releaseDockFocus,
} from "@/lib/dock-window";
import { warmSharedAudioContext } from "@/lib/voice/shared-audio-context";
import { useLine } from "@/components/composer/live/useLine";
import { useTalkDriver } from "@/components/composer/live/useTalkDriver";
import { BallStage } from "@/components/composer/live/BallStage";
import { useTaskSettled } from "@/components/composer/live/useTaskSettled";
import { useDockShellParts } from "@/components/composer/live/DockShellParts";
import { useLineAttach } from "@/components/composer/live/useLineAttach";
import {
  liveBashRef,
  liveEnergyRef,
  liveTalkRef,
  useLiveStore,
} from "@/components/composer/live/liveStore";
import { useDockRest } from "./useDockRest";

/** The ball's diameter in the dock; 128 until the founder picks. */
const DOCK_BALL_SIZE = 84;
/** The same ball on a phone, where 84 px crowds the screen. */
const DOCK_BALL_SIZE_MOBILE = 64;

/** How far a pointer may travel on the ball and still be a tap, in the
 *  window host (spec 2026-09-22 §4): past it the press is moving the panel. */
const DRAG_SLOP_PX = 6;

/** The ball has no attachment strip to say a native drop was too large. The
 *  native bridge fires only in the desktop shell, where the Line is off and
 *  the ball never renders: the box's own lane carries that error there. */
const NO_STRIP = (): void => {};

/** Where a key is text, not a gesture: an input, a textarea, anything
 *  editable (the rich editor is a contenteditable), and a surface that owns
 *  the keyboard outright. The computer pane's live browser is one: it forwards
 *  every key to the remote page (a focusable <video>, BrowserCanvas; the local
 *  lane's role="application", LocalBrowserSurface), and with the dock on the
 *  task screen it shares a screen with the ball. What is typed there is often
 *  a login: a letter must never open the type door (the pill would take the
 *  focus and the rest of a password would land in the composer), and a bare
 *  Control is the remote page's, never the line's. The rule is the surface
 *  that owns its keys, never the tag: a film in the story is a <video> nobody
 *  made focusable, and it is the page. */
function isTypingSurface(t: EventTarget | null): boolean {
  if (!(t instanceof Element)) return false;
  if (t.tagName === "INPUT" || t.tagName === "TEXTAREA") return true;
  return (
    t.closest("[contenteditable]:not([contenteditable='false'])") !== null ||
    inTheComputer(t)
  );
}

/** The computer pane, by the marker the task layout renders it with
 *  (task/layout.tsx; the Dock may not edit the pane's own files). */
const COMPUTER_PANE = '[data-testid="computer-sheet"]';

/** The computer's keys (R-F12): the focus on a surface that owns the keyboard,
 *  or anywhere inside the computer pane (the terminal's line). Narrower than a
 *  typing surface on purpose: the Control pair and the Alt tap fire from any
 *  input, the pill's own included, and yield to this alone. */
function inTheComputer(t: EventTarget | null): boolean {
  return (
    t instanceof Element &&
    t.closest(`video[tabindex], [role='application'], ${COMPUTER_PANE}`) !==
      null
  );
}

/**
 * The settle watch (spec §6): the run mark in the glass leaves when the
 * task's dot settles, read off the same truth ladder the dot and the crown's
 * receipt render. The Dock mounts it only while the line is on and a task of
 * the line's is in flight, keyed on the birth, so the flag-off Dock
 * subscribes to nothing and every birth watches afresh. One unsettled
 * reading arms the settle: a follow-up on a task whose dot already reads
 * done keeps its mark until that task runs and settles again, and the
 * reading that set the mark is never the one that takes it. Renders nothing.
 */
function TaskSettleWatch({ taskId }: { taskId: string }) {
  const dispatch = useLiveStore((s) => s.dispatch);
  const settled = useTaskSettled(taskId);
  const armed = useRef(false);
  useEffect(() => {
    if (!settled) armed.current = true;
    else if (armed.current) dispatch({ type: "task_settled" });
  }, [settled, dispatch]);
  return null;
}

/** Which geometry the dock wears, and the anchor that geometry reads. */
type DockLeg = "home" | "task" | "main";
const LEG_ANCHOR: Record<DockLeg, string | null> = {
  home: ".bh-col",
  task: ".task-messages-area",
  main: null, // the main area's box is arithmetic; there is nothing to wait for
};
/** How long to wait for an anchor before moving anyway, so an unexpected layout
 *  cannot park the dock on the old screen's box for good. */
const LEG_WAIT_MS = 500;

/**
 * The leg the dock is DRESSED in, which trails the route until the new screen's
 * anchor exists.
 *
 * A route's anchor (.bh-col, .task-messages-area) can mount several frames after
 * the pathname does — Suspense, data, streaming. Switching the moment the
 * pathname changes means anchor()/anchor-size() answer with their `-calc`
 * fallbacks and the box sets off toward a PROVISIONAL target. When the anchor
 * lands mid-flight it moves that target, and a moved target RESTARTS that
 * property's transition from wherever it had got to — even a sub-pixel move
 * does. left and width almost never restart by the same amount, so they come
 * apart: measured against an anchor 8 frames late, 33% apart, with width still
 * travelling 50ms after left had settled. That is the "the width animates, then
 * the position follows" seen between /home and /library/abilities.
 *
 * So the leg waits. The dock holds the old screen's box — where it already is,
 * so nothing moves — then makes the whole journey in one leg against a target
 * that never changes, which is the only way left and width stay together.
 */
function useDockLeg(onHome: boolean, onTask: boolean): DockLeg {
  const want: DockLeg = onHome ? "home" : onTask ? "task" : "main";
  const [leg, setLeg] = useState<DockLeg>(want);
  useEffect(() => {
    if (leg === want) return;
    const selector = LEG_ANCHOR[want];
    if (!selector || document.querySelector(selector)) {
      setLeg(want);
      return;
    }
    let raf = 0;
    const deadline = Date.now() + LEG_WAIT_MS;
    const look = (): void => {
      if (document.querySelector(selector) || Date.now() > deadline) {
        setLeg(want);
        return;
      }
      raf = requestAnimationFrame(look);
    };
    raf = requestAnimationFrame(look);
    return () => cancelAnimationFrame(raf);
  }, [want, leg]);
  return leg;
}

export interface DockProps {
  /** "page" (default): the site's dock, placed by its leg inside the app's
   *  columns. "window": the desktop shell's floating panel (spec 2026-09-22
   *  §4), whose transparent frame IS the dock's place, so it centres in it. */
  host?: "page" | "window";
}

/**
 * The dock: the one composer on every (app) screen except /support and
 * /dev/*, and, while one-composer-everywhere is off, the task section (flag
 * on, the task screen wears it too: spec 2026-09-20). It
 * hosts the home's box (BashPill) on every dock route (founder 09-12: "keep
 * the same composer on every screen"; "retire the super old composer for
 * good now"; spec 2026-09-12 one-composer): with the Line on the ball is the
 * rest state and the box the typing state (the type door); with the Line off
 * the box is the composer outright, the face as its mark. The box owns the
 * engine (sends, the thread, the history, the pickers, files, the draft).
 * The Dock owns the place and its centring, the ball's stage and veil, the
 * door, the gestures, the settle watch, the mic prime, the slide-away and the
 * ball's drag-over surface.
 */
export function Dock({ host = "page" }: DockProps = {}) {
  const pathname = usePathname();
  const dockRef = useRef<HTMLDivElement>(null);
  /** The ball's stage: the drag-over surface while the ball is up. */
  const stageRef = useRef<HTMLDivElement>(null);

  // ── the Line (flag-gated; spec 2026-09-11) ──────────────────────────────
  // The ball is the composer, the box is the side door. One line app-wide:
  // useLine keeps the media at module scope, the store is the truth, and the
  // talk driver fills the refs the ball reads per frame. Flag off, none of
  // this renders and the mic choice is never primed. Off inside the desktop
  // shell too (useLineOn): its native push-to-talk already owns Control.
  const liveOn = useLineOn();
  // One composer face (founder law): in the shell's MAIN window with its
  // floating Dock on, the panel is the composer, so this Dock is a route
  // with no dock like /support (nothing drawn, no keys, no room kept). Never
  // true on the web, in the panel, or with the shell's flag off.
  const floatingDock = useFloatingDockOn();
  const dockVisible = isDockVisible(pathname) && !floatingDock;
  // The type door lives in the store, one app-wide, and the box is the door
  // on every route: open, the Dock hosts the box with the ball as its mark;
  // shut, the ball. The door's words ride the draft store, so a route change
  // keeps them and keeps the door.
  const door = useLiveStore((s) => s.door);
  const openDoor = useLiveStore((s) => s.openDoor);
  const historyOpen = useLiveStore((s) => s.historyOpen);
  const ballUp = liveOn && door === "ball";
  // On /home the ball sits under the work column (the centring below) and
  // the home draws the veil inside its work box (BashHome.tsx).
  const onHome = pathname === "/home";
  // One composer everywhere (spec 2026-09-20): with the flag on the task screen
  // is a dock route (isDockVisible says so), and three things are its own
  // there. The PLACE: the dock stands on the task's story column, never the
  // main area, so an open computer pane is never under the ball or the pill
  // (the root's classes below; the layout publishes the centre). The PHONE:
  // the computer is a full-screen sheet that covered the old box, so the dock
  // slides away under it, mounted all the same, the line still open. The
  // PREFILL: words waiting for the task in view open the door (below).
  const onTask = isDockOnTaskEnabled() && onTaskRoute(pathname ?? "");
  const dockLeg = useDockLeg(onHome, onTask);
  const selectedTaskId = useTaskStore((s) => s.selectedTaskId);
  // computerStore's persist middleware rehydrates from localStorage
  // synchronously at import time — before React's first CLIENT render, but
  // the server never has that value, so it always renders with the default
  // (empty) store. Cold-loading a task whose Computer was left open in a
  // previous session makes the client's hydration pass see paneOpen=true a
  // beat before the server's HTML ever could, and paneOpen gates classes
  // below (max-md:translate-y-full etc.) — a genuine hydration mismatch, not
  // cosmetic. Same fix as AppShell's computerInView / useIsMobile: hold it at
  // the SSR-safe default (false) until after mount.
  const rawPaneOpen = useComputerStore((s) =>
    onTask && selectedTaskId ? s.isOpen(selectedTaskId) : false
  );
  const isMobile = useIsMobile();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const paneOpen = mounted ? rawPaneOpen : false;
  const taskPrefill = useComposerDraftStore((s) =>
    onTask && selectedTaskId ? s.prefillByTask[selectedTaskId] : undefined
  );
  // The belt must not depend on focus (R-F11): where a click does not focus
  // the live browser (Safari), the focus stays on the body and the rest of a
  // login would be the dock's. So the Dock remembers where the LAST pointer
  // press landed, and inside the computer pane every key gesture below leaves
  // the page alone, as it does a typing surface. A press outside the pane
  // clears it, the route changing clears it (the cleanup), and so does the
  // pane leaving the screen: the press is kept as the pane's ELEMENT and read
  // through isConnected, so a press on the pane's own close button never
  // leaves the dock deaf. One capture listener, on a task route with the flag
  // on and nowhere else.
  const pressedPane = useRef<Element | null>(null);
  useEffect(() => {
    if (!onTask) return;
    const pd = (e: PointerEvent) => {
      const t = e.target;
      pressedPane.current =
        t instanceof Element ? t.closest(COMPUTER_PANE) : null;
    };
    document.addEventListener("pointerdown", pd, true);
    return () => {
      document.removeEventListener("pointerdown", pd, true);
      pressedPane.current = null;
    };
  }, [onTask]);
  const paneHasThePointer = useCallback(
    () => pressedPane.current?.isConnected === true,
    []
  );

  // A file dropped on the ball needs the box: the files are parked in the
  // home lane (composerDraftStore.homeFiles; BashPill takes them on mount)
  // and the door opens. The box's own drop handles its own surface (its
  // handlers stop propagation), and the native desktop bridge is scoped to
  // the stage, which is not on screen while the box is, so no drop ever
  // lands twice. On an open line that takes files (attach on the call) the
  // drop stays on the call instead: the card under the ball takes them.
  const lineFiles = useLineAttach();
  const { on: filesOn, stage: stageFiles } = lineFiles;
  const intakeFiles = useCallback(
    (files: File[]) => {
      if (filesOn) return stageFiles(files);
      useComposerDraftStore.getState().parkHomeFiles(files);
      openDoor();
    },
    [openDoor, filesOn, stageFiles]
  );
  const { dragHandlers, isDragOver } = useComposerFileDrop({
    handleFiles: intakeFiles,
    setImageError: NO_STRIP,
    rootRef: stageRef,
  });

  const {
    line,
    toggle: toggleLine,
    close: closeLineNow,
    handle: lineHandle,
  } = useLine(liveOn);
  const lineOpen = line.phase === "open";
  useTalkDriver(lineHandle, liveOn && lineOpen);
  const turn = useLiveStore((s) => s.turn);
  const resetTurn = useLiveStore((s) => s.resetTurn);
  // The mute rides the store, never the component: the level and the mic
  // track move in one call, and a remount mid-call finds the truth.
  const muted = useLiveStore((s) => s.muted);
  const setMuted = useLiveStore((s) => s.setMuted);
  const micTrouble = useLiveStore((s) => s.micTrouble);
  const toggleTheLine = useCallback(() => {
    void toggleLine();
  }, [toggleLine]);
  // The audio DEVICE settles off the click path too (2026-09-22, the wake):
  // the page's one AudioContext is built here, in idle time, long before
  // anyone calls. Constructing one opens the output device and that block
  // (300 to 470 ms on a real Mac) used to land on the ball's click and freeze
  // the wake. The Dock is mounted app-wide, so this is the page's own boot;
  // warmSharedAudioContext is a no-op once the context exists.
  useEffect(() => {
    warmSharedAudioContext();
  }, []);
  // The mic permission prompt settles off the click path: the ball's first
  // hover or focus primes it, once per Dock (primeMic latches itself too).
  // Never while a line is not off: one opened by the ⌃ tap left the module's
  // latch down, and a second request against a device already capturing can
  // hand back or interrupt the live track (Safari). Not latched then, so a
  // hover once the line is off still pays the prompt.
  const micPrimed = useRef(false);
  const primeOnHover = useCallback(() => {
    if (micPrimed.current || useLiveStore.getState().line.phase !== "off")
      return;
    micPrimed.current = true;
    primeMic();
  }, []);
  // A single ⌃ tap's toggle waits out the ⌃⌃ window in this timer. It lives
  // in a ref so no effect re-run (history opening, a phase flip from a server
  // push) can swallow it: only the second tap of a pair, the ball leaving the
  // screen, or unmount clear it.
  const lineTapTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearLineTap = useCallback(() => {
    if (lineTapTimer.current) clearTimeout(lineTapTimer.current);
    lineTapTimer.current = null;
  }, []);
  useEffect(() => () => clearLineTap(), [clearLineTap]);
  // An open line never outlives its ball. The Dock is mounted app-wide and
  // renders nothing where the app has no dock (/support, /dev/*, and the task
  // section while one-composer-everywhere is off), so when the ball leaves the
  // screen a line that is connecting or open closes (a closing one is already
  // on its way) and a pending ⌃ tap never fires. Nothing opens again on
  // return. With that flag on the ball is on the task screen too, so walking
  // into a task and back never touches the call: the line never breaks.
  useEffect(() => {
    if (!liveOn || dockVisible) return;
    clearLineTap();
    if (line.phase === "open" || line.phase === "connecting") {
      void closeLineNow();
    }
  }, [liveOn, dockVisible, line.phase, closeLineNow, clearLineTap]);
  // The box's history is a level, alive only while the door is open (the
  // store's closeDoor drops it) and the dock is on screen (this half): a
  // history row opens its task on /task/<id>, where the Dock renders
  // nothing while one-composer-everywhere is off, and the box must not come
  // back with the card up, unasked. Both Line flag states; the door itself
  // stays, with its words.
  // The Island is the same kind of level (composer panels, R-23): it used to
  // reset when the Dock unmounted, and now that the store holds it (so a tray
  // panel can open it from the box or the Line) it goes on the same beat, or
  // a route into a dock-less screen and back would find the tray still open.
  // The crown's panel is the third level of the same kind (R-24): the sheet
  // is drawn on the dock's own crown, so a route where the dock paints
  // nothing must not keep it, nor hand it back on the next dock route.
  useEffect(() => {
    if (dockVisible) return;
    useLiveStore.getState().closeHistory();
    useLiveStore.getState().setTrayOpen(false);
    useLiveStore.getState().setPanel(null);
  }, [dockVisible]);
  // The Dock unmounting (the app shell going away) takes the line with it the
  // same way, once: a connecting or open line closes, a closing one is
  // already on its way.
  useEffect(() => {
    if (!liveOn) return;
    return () => {
      const phase = useLiveStore.getState().line.phase;
      if (phase === "open" || phase === "connecting") void closeLineNow();
    };
  }, [liveOn, closeLineNow]);

  // iOS anchors position:fixed to the LAYOUT viewport, so the open keyboard
  // simply covers the composer — visualViewport is the only honest measure of
  // the occluded bottom band. Publish it as --hb-kb on the dock root; the CSS
  // bottom offset rides it (globals.css). resize + scroll both fire while the
  // keyboard animates and while iOS pans the visual viewport.
  useEffect(() => {
    if (!dockVisible) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const sync = () => {
      const covered = Math.max(
        0,
        window.innerHeight - vv.height - vv.offsetTop
      );
      dockRef.current?.style.setProperty("--hb-kb", `${Math.round(covered)}px`);
    };
    sync();
    vv.addEventListener("resize", sync);
    vv.addEventListener("scroll", sync);
    return () => {
      vv.removeEventListener("resize", sync);
      vv.removeEventListener("scroll", sync);
    };
  }, [dockVisible]);

  // The room under the dock, published on the document root (useDockPad).
  useDockPad(dockVisible, floatingDock, liveOn, ballUp);

  // ── composer voice v2 (the Tide) — browser-only ──────────────────────────
  // The island (⌥) is the fleet's door; the tray (done rail phase 3) promotes
  // it past voice v2; its green count is the only pixel that ever changes
  // (quiet-rim ruling).
  // One level app-wide (composer panels, spec 2026-09-19 §3.4): a tray panel
  // opens it from the pill or the Line, so the ⌥ tap, the box and the Line
  // read and write the same truth.
  const islandOpen = useLiveStore((s) => s.trayOpen);
  const setIslandOpen = useLiveStore((s) => s.setTrayOpen);
  // The shell's Dock window (contract 2026-09-23): its tasks, moment and Mac
  // state; its tasks are the island's rows there. Null on the page host and
  // in a plain tab at /dock (the hook reads isDockWindow), which keep the fleet.
  const shell = useDockShellParts(host === "window");
  const fleetRows = useFleet(dockVisible && !shell);
  const rest = useDockRest(host, line, shell?.rows ?? fleetRows);
  const closeIsland = useCallback(() => setIslandOpen(false), [setIslandOpen]);

  // ⌥ tap toggles the island (release edge, dirty-tracked so ⌥-chords stay
  // dead); esc closes; ANY click outside the island closes (founder 08-04).
  // Never hover-away.
  useEffect(() => {
    if (!dockVisible) return;
    let altHeld = false;
    let altDirty = false;
    const kd = (e: KeyboardEvent) => {
      if (e.key === "Alt" && !e.repeat) {
        // The shell's panel: Option is the shell's (its thread summon), so
        // the page's own tap never runs on top of it. Escape and the outside
        // click below still close the island there.
        if (host === "window") return;
        // The computer's keys are the computer's (R-F12): no tap of ours, and
        // this keydown leaves the key to the page. The keyup below still calls
        // preventDefault() on every Alt, this one included; it stops no
        // propagation, so the pane still receives the event.
        if (inTheComputer(e.target) || paneHasThePointer()) {
          altHeld = false;
          return;
        }
        altHeld = true;
        altDirty = false;
        e.preventDefault();
        return;
      }
      if (altHeld && e.key !== "Alt") altDirty = true;
      if (e.key === "Escape") {
        // A fold claims its key in the shell's dock window (the release
        // below yields to it); the page host is unchanged.
        if (host === "window" && useLiveStore.getState().trayOpen)
          e.preventDefault();
        setIslandOpen(false);
      }
    };
    const ku = (e: KeyboardEvent) => {
      if (e.key !== "Alt" || host === "window") return;
      e.preventDefault();
      if (altHeld && !altDirty)
        setIslandOpen(!useLiveStore.getState().trayOpen);
      altHeld = false;
    };
    const pd = (e: PointerEvent) => {
      const t = e.target as Element | null;
      // Only the island itself keeps it open (the pill's hover strip is gone —
      // quiet pill, founder 08-07; ⌥ is the island's only opener).
      if (t?.closest?.(".cv2-island")) return;
      setIslandOpen(false);
    };
    window.addEventListener("keydown", kd, true);
    window.addEventListener("keyup", ku, true);
    document.addEventListener("pointerdown", pd);
    return () => {
      window.removeEventListener("keydown", kd, true);
      window.removeEventListener("keyup", ku, true);
      document.removeEventListener("pointerdown", pd);
    };
  }, [dockVisible, setIslandOpen, paneHasThePointer, host]);

  // ⌃⌃ (double-TAP Control) toggles the box's history (founder 08-13 r2: the
  // key, not a mouse double-click — the bar's clicks stay word selection).
  // A tap = clean press+release with no other key in between, the same
  // dirty-tracking the ⌥ island toggle uses, so ⌃C and friends stay dead;
  // two clean taps inside the window = the gesture. Ctrl is free here while
  // dictation is parked (hold-⌃ was its gesture).
  // The Line's single ⌃ tap (flag-gated) rides the same clean-tap tracking
  // and the same clock: one verdict per tap, the second of a pair (history)
  // or a single (the toggle, held back for the window), never both.
  const CTRL_TAP_WINDOW_MS = 400;
  const lastCtrlTapRef = useRef(0);
  useEffect(() => {
    // The shell's panel: Control is the shell's (hold = talk, tap = type),
    // and its key monitor passes the key through to the page, so the page's
    // own tap and pair would run on top of a summon (a hold's release read
    // as a tap hung the line up). The shell owns them there.
    if (!dockVisible || host === "window") return;
    let held = false;
    let dirty = false;
    // The Line's tap has stricter manners than the pair (it opens a billed
    // session): a bare Control with no other modifier down, no click, wheel or
    // menu while held (Control-click is the context menu, Control+wheel the
    // zoom), and never from a typing surface. The pair keeps its own rules.
    let lineDirty = false;
    const kd = (e: KeyboardEvent) => {
      if (e.key === "Control" && !e.repeat) {
        // The computer's keys are the computer's (R-F12): a Control there is
        // no tap of ours, the single or either half of the pair. Not held, so
        // its release breaks the chain below.
        if (inTheComputer(e.target) || paneHasThePointer()) {
          held = false;
          return;
        }
        held = true;
        dirty = false;
        lineDirty =
          e.shiftKey || e.altKey || e.metaKey || isTypingSurface(e.target);
        return;
      }
      if (held && e.key !== "Control") dirty = true;
    };
    const soil = () => {
      if (held) lineDirty = true;
    };
    const ku = (e: KeyboardEvent) => {
      if (e.key !== "Control") return;
      const wasClean = held && !dirty;
      const lineClean = wasClean && !lineDirty;
      held = false;
      if (!wasClean) {
        lastCtrlTapRef.current = 0; // a chord breaks the chain
        return;
      }
      const at = Date.now();
      if (at - lastCtrlTapRef.current <= CTRL_TAP_WINDOW_MS) {
        // The pair: history takes it, and the toggle the first tap armed is off.
        lastCtrlTapRef.current = 0;
        clearLineTap();
        // The card is the box's (PillHistory inside the box): while the ball
        // is up the pair opens the door and the box mounts with it up.
        const live = useLiveStore.getState();
        if (liveOn && live.door === "ball") live.openDoor();
        live.toggleHistory();
        return;
      }
      lastCtrlTapRef.current = at;
      if (!liveOn || !lineClean) return;
      // The single: the toggle fires once the window has passed and spends the
      // tap, so a tap landing as it fires starts a new chain, never the pair.
      clearLineTap();
      lineTapTimer.current = setTimeout(() => {
        lineTapTimer.current = null;
        lastCtrlTapRef.current = 0;
        toggleTheLine();
      }, CTRL_TAP_WINDOW_MS);
    };
    window.addEventListener("keydown", kd, true);
    window.addEventListener("keyup", ku, true);
    window.addEventListener("mousedown", soil, true);
    window.addEventListener("wheel", soil, true);
    window.addEventListener("contextmenu", soil, true);
    return () => {
      window.removeEventListener("keydown", kd, true);
      window.removeEventListener("keyup", ku, true);
      window.removeEventListener("mousedown", soil, true);
      window.removeEventListener("wheel", soil, true);
      window.removeEventListener("contextmenu", soil, true);
    };
  }, [
    dockVisible,
    liveOn,
    toggleTheLine,
    clearLineTap,
    paneHasThePointer,
    host,
  ]);

  // The starters' prefill: the box takes the words on its own mount (its
  // prefill law), so while the ball is up the Dock only opens the door.
  const homePrefill = useComposerDraftStore((s) => s.homePrefill);
  useEffect(() => {
    if (!homePrefill || !ballUp) return;
    openDoor();
  }, [homePrefill, ballUp, openDoor]);
  // The task in view's own prefill lane (the shelf's take-back, the Try chip,
  // Studio's retry, the door's handed-back words): the box takes the words on
  // its mount (the pill's task pickers), so while the ball is up the Dock only
  // opens the door, the home lane's rule above. An edge, never a level: the
  // door opens ONCE per draft object (requestPrefill mints a new one each
  // time), at the Dock's first sight of it, door open or shut. Words nobody
  // consumed (the pending placeholder has no task pickers to take them) must
  // not hold the door open against the person closing it, nor open it again
  // on every return to the task.
  const prefillSeen = useRef<typeof taskPrefill>(undefined);
  useEffect(() => {
    if (!taskPrefill || prefillSeen.current === taskPrefill) return;
    prefillSeen.current = taskPrefill;
    if (ballUp) openDoor();
  }, [taskPrefill, ballUp, openDoor]);

  // The Line's keys. Esc ends the line only when it is plainly meant for it:
  // in the bubble phase, not already claimed (a palette, a modal or a menu
  // preventDefaults its own), never while the history is up (its own Esc),
  // and only from the ball, the stage, or nowhere in particular (the body).
  // Any letter while the ball is up and the line is open is the type door,
  // the letter riding into the box's draft through the store. Space is left
  // to the focused ball (it is the button's own key). The ⌃ tap toggle lives
  // with the ⌃⌃ history gesture above (one clock, one verdict).
  useEffect(() => {
    if (!liveOn || !dockVisible) return;
    const onEscape = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented || historyOpen) return;
      if (paneHasThePointer()) return;
      const t = e.target;
      const onBall =
        t instanceof Element && t.closest(".cl-orb, .cl-stage") !== null;
      if (!onBall && t !== document.body) return;
      if (useLiveStore.getState().line.phase !== "off") void closeLineNow();
    };
    const kd = (e: KeyboardEvent) => {
      if (door !== "ball" || !lineOpen) return;
      // Mid-IME-composition the key is not a letter yet.
      if (e.isComposing) return;
      if (e.key.length !== 1 || e.key === " ") return;
      // ⌃ and ⌥ together is AltGr (a letter on Windows layouts); either alone,
      // or ⌘, is a chord.
      if (e.metaKey || e.ctrlKey !== e.altKey) return;
      if (isTypingSurface(document.activeElement)) return;
      if (paneHasThePointer()) return;
      e.preventDefault();
      openDoor(e.key);
    };
    window.addEventListener("keydown", onEscape);
    window.addEventListener("keydown", kd, true);
    return () => {
      window.removeEventListener("keydown", onEscape);
      window.removeEventListener("keydown", kd, true);
    };
  }, [
    liveOn,
    dockVisible,
    door,
    lineOpen,
    historyOpen,
    closeLineNow,
    openDoor,
    paneHasThePointer,
  ]);

  // detect if any foreign inputs are in focus
  const [isForeignInputFocused, setIsForeignInputFocused] = useState(false);
  useEffect(() => {
    if (!dockVisible) return;
    const isMobileDevice = () => {
      // jsdom has no matchMedia — the guard keeps the feature a no-op in
      // tests instead of throwing.
      if (typeof window === "undefined") return false;
      if (typeof window.matchMedia !== "function") return false;
      const hasCoarsePointer = window.matchMedia("(pointer: coarse)").matches;
      const isMobileUA =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );
      return hasCoarsePointer || isMobileUA;
    };
    if (!isMobileDevice()) return;

    const isEditableElement = (el: EventTarget | null): el is HTMLElement => {
      if (!(el instanceof HTMLElement)) return false;
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
        if (el.readOnly || el.disabled) return false;
      }
      if (el instanceof HTMLInputElement) {
        const keyboardTypes = [
          "text",
          "password",
          "email",
          "number",
          "search",
          "tel",
          "url",
        ];
        return keyboardTypes.includes(el.type);
      }
      return el instanceof HTMLTextAreaElement || el.isContentEditable;
    };
    const handleFocusIn = (e: FocusEvent): void => {
      if (!isMobileDevice()) return;
      const target = e.target;
      if (isEditableElement(target) && !dockRef.current?.contains(target)) {
        setIsForeignInputFocused(true);
      }
    };
    const handleFocusOut = (e: FocusEvent): void => {
      const relatedTarget = e.relatedTarget;
      if (
        isEditableElement(relatedTarget) &&
        !dockRef.current?.contains(relatedTarget)
      ) {
        return;
      }
      setIsForeignInputFocused(false);
    };
    document.addEventListener("focusin", handleFocusIn);
    document.addEventListener("focusout", handleFocusOut);
    return () => {
      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("focusout", handleFocusOut);
    };
  }, [dockVisible]);

  // ── the window host (spec 2026-09-22 §4) ────────────────────────────────
  // The shell's panel holds this Dock and nothing else, so four things are
  // the panel's own: the rect the shell grants clicks inside, the ball as the
  // panel's drag handle, the summons the shell's keys send, and Escape
  // handing the keyboard back. Every one is gated on host === "window", so
  // the page host runs none of them and keeps today's bytes.

  // 1. The shell grants clicks only inside the rect the page reports (see
  //    useDockWindowRect): the transparent margin of the fixed panel must stay
  //    click-through.
  useDockWindowRect(host, dockRef, ballUp);

  // 2. The ball is the drag handle: a pointer that travels past the slop
  //    before it lifts moves the whole panel, and the click that follows is
  //    swallowed so a drag never also toggles the line. A still pointer is a
  //    tap, which the ball keeps. Only the press arms this, so on the page
  //    host (where the press returns at once) move, lift and click are inert.
  const dragRef = useRef<{ x: number; y: number; dragging: boolean } | null>(
    null
  );
  const onStagePointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (host !== "window" || e.button !== 0) return;
      dragRef.current = { x: e.clientX, y: e.clientY, dragging: false };
    },
    [host]
  );
  const onStagePointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const d = dragRef.current;
      if (!d || d.dragging) return;
      if (Math.hypot(e.clientX - d.x, e.clientY - d.y) < DRAG_SLOP_PX) return;
      d.dragging = true;
      beginDockDrag();
    },
    []
  );
  const onStagePointerUp = useCallback(() => {
    // A tap is forgotten at once. A drag is kept for the click it ends with,
    // which the capture below swallows and then forgets. The OS owns the
    // pointer once the panel is moving, so a drag's lift or click may never
    // arrive here: the next press re-arms this from scratch, which is what
    // keeps a swallowed click from outliving its drag.
    if (!dragRef.current?.dragging) dragRef.current = null;
  }, []);
  const onStageClickCapture = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      if (!dragRef.current?.dragging) return;
      dragRef.current = null;
      e.stopPropagation();
      e.preventDefault();
    },
    []
  );

  // 3. The shell's summons (ptt.rs): hold Control = talk, tap Control = type,
  //    tap Option = the task shelf (the island, the notch's ⌥; the thread
  //    stays on the pill). Talk STARTS a line that is off and leaves a
  //    live one listening (spec §4: "start the line if idle; a live line just
  //    keeps listening") — this codebase's idle is phase "off", and a line
  //    that is connecting or closing is mid-answer already. The phase is read
  //    when the summon lands, never closed over: a subscription that re-armed
  //    on every phase change would be detached for the round trip of the
  //    shell's unlisten and listen, and a hold landing in it would be lost.
  useEffect(() => {
    if (host !== "window") return;
    return onDockSummon((mode) => {
      const live = useLiveStore.getState();
      if (mode === "talk") {
        if (live.line.phase === "off") toggleTheLine();
        return;
      }
      if (mode === "type") {
        openDoor();
        return;
      }
      live.setTrayOpen(!live.trayOpen);
    });
  }, [host, toggleTheLine, openDoor]);

  // 4. Escape hands the keyboard back to the app the person was in: the
  //    panel takes focus to type and must never keep it. The island, the
  //    history and the thread fold first: each CLAIMS its Escape
  //    (preventDefault) in the window host, so that key keeps the panel's
  //    focus and the next releases. The door is the exception (the plan's
  //    Task 3): one Escape after typing folds the box AND hands the keyboard
  //    back, so its closer leaves the key unclaimed. That closer is the
  //    editor's React handler, which runs at the root before the key reaches
  //    this window listener, so the store already says "ball" here.
  //    Claims are read off the event, never off render timing. The history's
  //    closer is a bubble listener that may be registered after this one, so
  //    what is still up in the store is checked too: a closer that ran
  //    earlier claimed the key, one that runs later has not closed anything
  //    yet, so either way the key is not the release's.
  useEffect(() => {
    if (host !== "window") return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      const live = useLiveStore.getState();
      if (live.door !== "ball" || live.historyOpen || live.trayOpen) return;
      releaseDockFocus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [host]);

  if (!dockVisible) return null;

  const rootClass = cn(
    "hb-dock fixed bottom-(--site-dock-bottom) z-(--site-dock-z-index)",
    // The shell's dock window (spec 2026-09-22 §4): the panel holds this dock
    // and nothing else, so the frame IS its place and the root simply spans it.
    // A left edge and a width, the two place legs under it, read the app's own
    // columns — a sidebar, a main area — which the panel does not have, so they
    // are the page host's alone. The task leg needs no such gate: the panel's
    // route is /dock, never a task one, so onTask is false there and the leg
    // cannot fire. Every web call site passes no host, which is why the page's
    // classes are today's bytes.
    //
    // A LEFT AND RIGHT EDGE, never `flex justify-center`. The children below
    // are blocks that fill the root and centre their own contents (the stage's
    // own justify-center holds the ball); made flex ITEMS they would shrink to
    // fit the ball instead, taking the stage's drag-over surface and the veil's
    // max-w-full cap down with them.
    host === "window" && "inset-x-0",
    // On /home it spans the work column (founder 09-12: "center bash under the
    // tasks not wrt whole screen"), so that it reads as part of the same box:
    // main-wide on the column's centre, its left edge would cover the sidebar's
    // account card. The pair is a LEFT EDGE and a width, never a centre — a
    // centre turns any width error into a gap at both edges, and it is the start
    // of the column the eye lines up. That also spends no transform, so the
    // slide-away below keeps translate-y to itself. One geometry at every width:
    // below the fold .bh-page is a single column, so the column already IS the
    // phone's box, and no max-md: branch may ship here to out-specify it — which
    // is why this leg is exclusive with the one under it rather than layered on
    // top of it. Where the two answers behind these come from, and why neither a
    // hand-written calc nor a ResizeObserver was one: globals.css, beside
    // --site-home-col-left.
    host === "page" &&
      dockLeg === "home" &&
      "left-(--site-home-col-left) w-(--site-home-col-width)",
    // Everywhere else the box sits on the main area, capped at its own width,
    // and hugs the phone's gutters. A LEFT EDGE here too (globals.css
    // --site-main-box-left): this leg used to centre with -translate-x-1/2,
    // and a translate-x that differs from /home's animated half the dock's
    // width sideways across every navigation between them — the rubber band.
    host === "page" &&
      dockLeg !== "home" &&
      "max-md:left-(--site-main-box-left-mb) left-(--site-main-box-left) max-md:w-(--site-main-center-width-mb) w-(--site-main-box-width)",
    // On a task screen (one composer everywhere) the dock stands on the story
    // column, from md up: the layout publishes the column's centre, and both the
    // width and the left edge derive from it (globals.css --site-task-col-*).
    // Unpublished, the main area's are the fallbacks. Below md the phone's own
    // box above stands, so this
    // one LAYERS on that leg (md: is its own merge group) instead of replacing
    // it — a task route is never /home, so it always lands on the main-area leg.
    dockLeg === "task" &&
      "md:left-[var(--site-task-col-left,var(--site-main-box-left))] md:w-[var(--site-task-col-width,var(--site-main-box-width))]",
    // Named, because the two halves need different curves: left/width glide on
    // the sidebar's 0.2s --ease-smooth, opacity/translate keep the shipped 500ms
    // overshoot for the slide-away (utilities.css). Tailwind's own `transition`
    // listed neither left nor width, so the box jumped to its new place and THEN
    // slid — half its width sideways, springing back off the overshoot curve.
    "transition-dock",
    isForeignInputFocused
      ? "translate-y-full opacity-0 pointer-events-none"
      : "translate-y-0 opacity-100 pointer-events-auto",
    // The phone's computer is a sheet over the whole screen (fixed inset-0
    // z-40) that covered the old box: under it the dock slides away, below md
    // only. Paint, not presence: the route still has its dock and its line.
    // invisible makes it airtight: the crown and the tray set pointer-events:
    // auto themselves (.cl-crown[data-up="1"], .cl-tray[data-up]), and after
    // the slide the crown lands ON screen over the sheet, unseen, a receipt
    // link in it. visibility: hidden takes the subtree out of hit-testing, the
    // tab order and the accessibility tree, and nothing in the dock turns it
    // back on. It is not in the transition's list: out is a cut, back slides.
    paneOpen &&
      "max-md:translate-y-full max-md:opacity-0 max-md:pointer-events-none max-md:invisible",
    // The page runs under the hovering ball (the veil): the root's empty
    // flanks must not eat a click or a drop meant for a card beneath. Only
    // the stage takes pointer events back (below). The type door is the box,
    // which fills the root: the root stays live.
    ballUp && "pointer-events-none"
  );

  // The settle watch rides every rendered branch: a task born through the
  // line keeps running behind the type door too. It paints nothing.
  const settleWatch =
    liveOn && line.runningTaskId !== null ? (
      <TaskSettleWatch key={line.births} taskId={line.runningTaskId} />
    ) : null;

  return (
    <div ref={dockRef} className={rootClass} {...rest}>
      {settleWatch}
      <TrayIsland
        rows={shell?.rows ?? fleetRows}
        // The popover is portaled out of the root, so the root's own ways of
        // going away (slid off behind a foreign input; invisible under the
        // phone's computer sheet) no longer take the island with it.
        open={islandOpen && !isForeignInputFocused && !(paneOpen && isMobile)}
        onClose={closeIsland}
        anchorRef={dockRef}
        hasUser
      />
      {ballUp ? (
        /* The ball is the composer (the Line): the stage sits where the box
           sits, and "or type" under it opens the door on the box in its place.
           The ball's first hover or focus settles the mic prompt off the click
           path (primeMic latches itself) — on the BALL, passed down as onPrime,
           never on this wrapper: it holds "or type" and the whisper row too, and
           React's onFocus rides native focusin, which bubbles, so a click on
           "or type" used to ask for the microphone. The stage is the ball's
           drag-over surface: a drop here parks the files for the box and opens
           the door.
           As wide as the root, so the pointer events come back on the stage
           alone (the ball, its row, the tray, the crown), not on this box. */
        <div
          ref={stageRef}
          className={cn(
            "hb-dock__stage relative flex justify-center py-2 [animation:clFadeIn_0.35s_var(--ease-smooth)_backwards]",
            // The stage takes pointer events back through the root's none
            // only while the dock is on screen: slid away behind a foreign
            // input (opacity 0, off the bottom edge) the ball keeps no hit
            // band on mobile web, the same rule the root wears.
            !isForeignInputFocused &&
              (paneOpen
                ? "md:[&>.cl-stage]:pointer-events-auto"
                : "[&>.cl-stage]:pointer-events-auto")
          )}
          onDragEnter={dragHandlers.onDragEnter}
          onDragLeave={dragHandlers.onDragLeave}
          onDragOver={dragHandlers.onDragOver}
          onDrop={dragHandlers.onDrop}
          /* The panel's drag handle (the window host; inert on the page).
             These ride the stage the ball already has rather than a wrapper
             of their own: the stage grants the ball its pointer events
             through a DIRECT-child selector ([&>.cl-stage]), which one more
             element between them would break. */
          data-testid={host === "window" ? "dock-ball-stage" : undefined}
          onPointerDown={onStagePointerDown}
          onPointerMove={onStagePointerMove}
          onPointerUp={onStagePointerUp}
          onPointerCancel={onStagePointerUp}
          onClickCapture={onStageClickCapture}
        >
          {/* The veil (founder 09-12: the same hovering composer on every
              screen): the page's content runs under the ball and dissolves
              into the page colour around it. ONE veil, the dock's, on every
              route including /home: the home drew its own inside the work box
              until that box stopped stretching for the ball (BashHome.tsx).

              A DOME, not a panel. It was a linear fade in a rectangle, so only
              its top edge dissolved and its left and right cut the page off
              dead straight. This is a circle centred on the stage's bottom
              edge, on the ball's own vertical line.

              --cl-veil-r IS the radius, as a share of the stage's own height,
              and it is the only number here: the box is twice it and sits half
              it below the stage, which is what keeps the centre ON that bottom
              edge. Raise the radius alone and the centre rides up with it.
              Percentages both resolve against the stage's height (bottom and
              height both do), and closest-side makes the painted radius half
              the box, so the three stay in step by construction. The stage's
              height IS the ball plus the row under it plus the padding, so the
              dome follows any change to either: at the shipped 84px ball the
              stage is 140px, the ball's top corners sit 138.5px out, and 120%
              puts the radius at 168px.

              It no longer takes a width from the screen it is on: the task
              screen's `md:w-[calc(100%+32px)]` spanned the story column, which
              a circle sized off the stage has no use for.

              `max-w-full` is the one thing it still owes the screen. Sized off
              the stage's HEIGHT, the dome is ~520px across whatever the column
              is, so beside an open computer pane at the 400px seam it laid 74px
              of porcelain over the pane — the fade the founder reported
              (2026-09-21). The stage is as wide as the root, and the root's box
              is the column's, so 100% IS the column. closest-side then takes
              the radius off the capped width and the dome stays a circle, just
              a smaller one. A no-op everywhere the column is wider than the
              dome, which is /home, a phone and every other screen.

              The page host's alone. The dome blends the ball into the page
              BEHIND it, and the shell's dock window has no page: its document
              is forced transparent (app/(protected)/dock/layout.tsx) so the
              ball floats over the desktop. Painted there, an opaque --bg-base
              disc is exactly what the panel is transparent to avoid. */}
          {host === "page" && (
            <div
              aria-hidden
              className="hb-dock__veil pointer-events-none absolute left-1/2 -translate-x-1/2 -z-10 aspect-square max-w-full [--cl-veil-r:140%] h-[calc(var(--cl-veil-r)*2)] bottom-[calc(-1*var(--cl-veil-r))] bg-[radial-gradient(circle_closest-side,var(--bg-base)_60%,transparent_100%)]"
            />
          )}
          {/* The work of a spoken turn rides the stage (the crown). A typed
              ask and the history live in the box, which is never on screen
              with the stage, so the turn is the stage's whole. */}
          <BallStage
            line={line}
            onToggle={toggleTheLine}
            onType={() => openDoor()}
            onPrime={primeOnHover}
            size={isMobile ? DOCK_BALL_SIZE_MOBILE : DOCK_BALL_SIZE}
            turn={turn}
            onDismissReceipt={resetTurn}
            energyRef={liveEnergyRef}
            bashRef={liveBashRef}
            talkRef={liveTalkRef}
            onMute={setMuted}
            muted={muted}
            micTrouble={micTrouble}
            crown={shell?.crown}
            aside={shell?.aside}
            hostNote={shell?.note}
            attach={lineFiles}
            dragOver={isDragOver}
          />
        </div>
      ) : (
        /* The box: the type door while the Line is on (it fades in when the
           door opens, opacity only, never a transform), the composer outright
           while it is off. It takes focus, the parked draft and the parked
           files on its own mount (BashPill.tsx). */
        <div
          className={cn(
            liveOn && "[animation:clFadeIn_0.3s_var(--ease-smooth)_backwards]"
          )}
        >
          <BashPill />
        </div>
      )}
    </div>
  );
}
